/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2018, 2019
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
import DnDHandler from './DnDHandler';
import $ from 'jquery';
import SpecUtils from 'core/WidgetSpecUtil';

class SingleVisDnDHandler extends DnDHandler {
	constructor(options) {
		super(options);
		this.dashboardApi = options.dashboardApi;
		this.createVis = options.createVis;
		this.replaceWithWidget = options.replaceWithWidget;
		this.placeWidgetInCard = options.placeWidgetInCard;
		this.widgetLayout = options.widgetLayout;
		this.layoutModel = options.layoutModel;
	}

	getNewCompareDnDEnabled() {
		return this.layoutModel && this.layoutModel.isNewCompareDnDFeature();
	}

	// Since we use opacity to determine selected card in explore and all cards are stacked 
	// with opacity 0(except the selected one), DNDManager does not know which dropzones belong to which card, or other
	// drop targets in the layout(cardlist, starting points, etc)
	// Even though card with opacity 1(selected card) may be visible, there might be other listening drop targets 
	// in front of it with opacity 0, resulting in partial or complete highlight target deactivation on hover action.
	// DNDManager is used across entire product stack, therefore this check is isolated to Explore where we employ 
	// opacity for current card.
	isVisible() {
		let n = this.domNode;
		while (n && n !== document) {
			if (!n.getClientRects().length || $(n).css('opacity') === '0') {
				return false;
			}
			n = n.parentElement;
		}
		return true;
	}

	accepts(dragObject) {
		if (!this.isVisible()) {
			return false;
		}

		if (this.getNewCompareDnDEnabled() && (dragObject.type === 'card' && dragObject.data.length === 1)) {
			const card = this.getCardById(dragObject.data[0]);
			return card.subType !== 'compare'; // prevent dropping compare card onto vis template
		}
		if (this.replaceWithWidget && !this.widgetLayout) {
			return false;
		}
		if (this.isPinObject(dragObject)) {
			const liveWidgets = this.getPinWidgets(dragObject);
			return liveWidgets.length === 1;
		} else if (this.isAcceptedLiveWidget(dragObject)) {
			return true;
		}
		return false;
	}

	onDrop(dragObject) {
		const isDnDFromCardList = (dragObject.type === 'card' && dragObject.data.length === 1) || false;
		return this.getWidgetSpec(dragObject).then(widgetSpec => {
			if (widgetSpec) {
				const shouldCreate = isDnDFromCardList ? 'cardCreate' : undefined;
				this.createVis && this.createVis(widgetSpec);
				this.replaceWithWidget && this.replaceWithWidget(widgetSpec, this.widgetLayout.id, shouldCreate);
			}
		});
	}

	getWidgetSpec(dragObject) {
		if (dragObject.type === 'card') {
			const card = this.getCardById(dragObject.data[0]);
			return SpecUtils.getWidgetSpecForCompareCard(card, this.dashboardApi);
		} else if (this.isPinObject(dragObject)) {
			return this.getSpecfromPin(dragObject)
				.then(spec => this._getSpec(spec, 'fragment'));
		} else if (this.isAcceptedLiveWidget(dragObject)) {
			return this._getSpec(dragObject.data.model, 'widget');
		}
		return Promise.resolve(null);
	}

	/**
	 * @param {String} id of the Card
	 * Return Card object
	 */
	getCardById(id) {
		return this.dashboardApi.getCurrentContentView().getExploreContainerLayoutModel().findModel(id);
	}

	_getSpec(spec, specType) {
		return Promise.resolve({
			model: spec,
			specType: specType
		});
	}
}

export default SingleVisDnDHandler;



// WEBPACK FOOTER //
// ./src/js/util/SingleVisDnDHandler.js